// Kho dữ liệu thẻ liệu trình (mock, lưu localStorage — sẽ thay bằng API khi có backend)
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { SEED_CUSTOMERS, STAFF, type Customer, type Order, type PaymentMethod } from '../POS/data';
import {
  DEFAULT_CONFIG, policyOf, pricingOf, seedPackages, serviceOf, type PayRequest,
  type BookingStatus, type CardBaseStatus, type CardBooking, type CardEvent, type CardRefund, type CardUsage, type Installment,
  type TreatmentCard, type TreatmentConfig, type TreatmentPackage,
} from './data';
import { activationOf, addDaysYmd, buildLines, issueValidity, usageItems, ymd, type UsageRequestItem } from './logic';
import { cardFromOrder, reconcile, type PosSnapshot } from './posSync';

export const TREATMENT_STORAGE_KEY = 'khothi-treatment-v1';

const pad = (n: number, w: number) => String(n).padStart(w, '0');
const fmt = (n: number) => new Intl.NumberFormat('vi-VN').format(Math.round(n));

type SeqKey = 'card' | 'use' | 'booking' | 'pay' | 'refund';
type Seq = Record<SeqKey, number>;
const PREFIX: Record<SeqKey, string> = { card: 'TLT-', use: 'SD-', booking: 'LH-', pay: 'TT-', refund: 'HTT-' };
const idOf = (k: SeqKey, n: number) => `${PREFIX[k]}${pad(n, 6)}`;

export interface DeductInput {
  items: UsageRequestItem[];
  branchId: string;
  staffId: string;
  note: string;
  signed: boolean;
  bookingId?: string;
  overrideBy?: string;
  overrideReason?: string;
  by: string;
}

interface TreatmentState {
  packages: TreatmentPackage[];
  cards: TreatmentCard[];
  config: TreatmentConfig;
  seq: Seq;
  /** Mốc tự phát hành thẻ từ đơn POS */
  syncSince: string;
  /** Yêu cầu thu tiền gửi sang POS */
  payRequests: PayRequest[];
  reqSeq: number;

  upsertPackage: (p: TreatmentPackage, by: string) => void;
  togglePackage: (id: string) => void;
  setConfig: (patch: Partial<TreatmentConfig>) => void;

  reconcileWithPos: (snap: PosSnapshot) => TreatmentCard[];
  issueCard: (input: { order: Order; lineKey: string; customer: Customer; plan: Installment[]; consultant: string; by: string }) => TreatmentCard | null;
  setPlan: (cardId: string, plan: Installment[], by: string, reason: string) => void;
  recordPayment: (cardId: string, p: { amount: number; method: PaymentMethod; txnRef: string; by: string; branchId: string }) => void;
  deduct: (cardId: string, input: DeductInput) => CardUsage | null;
  cancelUsage: (cardId: string, usageId: string, reason: string, by: string) => void;
  extendCard: (cardId: string, input: { expiresAt: string; months: number; fee: number; reason: string; by: string }) => void;
  adjustCard: (cardId: string, input: { serviceId?: string; delta: number; reason: string; by: string }) => void;
  createBooking: (cardId: string, input: Omit<CardBooking, 'id' | 'status' | 'createdAt' | 'name'>) => CardBooking;
  setBookingStatus: (cardId: string, bookingId: string, status: BookingStatus, by: string, reason?: string) => void;
  addRefund: (cardId: string, r: Omit<CardRefund, 'id' | 'at'>) => CardRefund;
  approveRefund: (cardId: string, refundId: string, by: string) => void;
  rejectRefund: (cardId: string, refundId: string, by: string, reason: string) => void;
  /** Ghi nhật ký in phiếu / gửi phiếu cho khách */
  logPrint: (cardId: string, by: string, text: string) => void;
  createPayRequest: (input: { cardId: string; amount: number; installmentLabel: string; branchId: string; note: string; by: string }) => PayRequest | null;
  cancelPayRequest: (id: string, by: string, reason: string) => void;
  rejectPayRequest: (id: string, by: string, reason: string) => void;
  completePayRequest: (id: string, r: { paymentId: string; amountPaid: number; method: PaymentMethod; txnRef: string; change: number; tendered: number; by: string }) => void;
  resetDemo: () => void;
}

// ---------------------------------------------------------------------------
// Dữ liệu mẫu: thẻ chuyển từ MySpa với lịch sử thanh toán, sử dụng, lịch hẹn

interface SeedSpec {
  customerId: string;
  pkgId: string;
  soldAgo: number;
  branchId: string;
  consultant: string;
  discountPct?: number;
  /** Ngày sau khi bán + số tiền (đợt cuối bỏ trống = phần còn lại) */
  plan: [number, number?][];
  pays: [number, number, PaymentMethod][];
  /** Số ngày trước hôm nay, [dịch vụ, số buổi][], mã KTV */
  uses: [number, [string, number][], string][];
  bookings?: [number, number, number, string, string, BookingStatus?][];
  refund?: { ago: number; feePct: number; reason: string; method: PaymentMethod };
  overrideUse?: number;
}

function buildSeed(now = new Date()) {
  const packages = seedPackages(now);
  const seq: Seq = { card: 0, use: 0, booking: 0, pay: 0, refund: 0 };
  const next = (k: SeqKey) => idOf(k, ++seq[k]);
  const at = (daysOffset: number, h: number, m = 0) => {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysOffset, h, m);
    return d.toISOString();
  };
  const staffBranch = (id: string) => STAFF.find(s => s.id === id)?.branchId || 'q1';
  const staffLabel = (id: string) => STAFF.find(s => s.id === id)?.name || id;

  const specs: SeedSpec[] = [
    { customerId: 'c1', pkgId: 'k1', soldAgo: 120, branchId: 'q1', consultant: 'Lan', plan: [[0, 20_000_000], [30, 20_000_000], [60]], pays: [[0, 20_000_000, 'transfer'], [28, 20_000_000, 'transfer'], [61, 20_000_000, 'card']],
      uses: [[115, [['s2', 1]], 'lan'], [85, [['s2', 1]], 'mai'], [55, [['s2', 1]], 'lan'], [20, [['s2', 1]], 'ha']], bookings: [[3, 10, 0, 's2', 'lan']] },
    { customerId: 'c2', pkgId: 'k7', soldAgo: 200, branchId: 'q3', consultant: 'Minh', plan: [[0]], pays: [[0, 20_000_000, 'transfer']],
      uses: [[180, [['s3', 1]], 'minh'], [150, [['s4', 1]], 'thao'], [140, [['s5', 1]], 'thao'], [120, [['s3', 1]], 'minh'], [90, [['s4', 1]], 'minh'], [60, [['s5', 1]], 'thao'], [40, [['s6', 1]], 'thao'], [15, [['s4', 1]], 'minh']] },
    { customerId: 'c3', pkgId: 'k2', soldAgo: 75, branchId: 'q1', consultant: 'Mai', plan: [[0, 5_000_000], [45]], pays: [[0, 5_000_000, 'cash']],
      uses: [[70, [['s6', 1]], 'mai'], [60, [['s6', 1]], 'mai'], [50, [['s6', 1]], 'ha'], [40, [['s6', 1], ['t4', 1]], 'mai'], [25, [['s6', 1]], 'lan']], bookings: [[0, 16, 0, 's6', 'mai']] },
    { customerId: 'c4', pkgId: 'k3', soldAgo: 160, branchId: 'q1', consultant: 'Hà', plan: [[0]], pays: [[0, 6_000_000, 'card']],
      uses: [[150, [['s5', 1], ['t2', 1]], 'ha'], [135, [['s5', 1]], 'ha'], [120, [['s5', 1], ['t2', 1]], 'mai'], [100, [['s5', 1]], 'ha'], [85, [['s5', 1], ['t2', 1]], 'ha'], [70, [['s5', 1]], 'lan'], [50, [['s5', 1], ['t2', 1]], 'ha'], [30, [['s5', 1]], 'ha'], [0, [['s5', 1]], 'mai']] },
    { customerId: 'c5', pkgId: 'k4', soldAgo: 100, branchId: 'q3', consultant: 'Thảo', plan: [[0, 1_600_000], [30, 1_600_000], [60]], pays: [[0, 1_600_000, 'ewallet']],
      uses: [[95, [['t1', 1]], 'thao'], [60, [['t1', 1]], 'thao'], [30, [['t1', 1]], 'minh']], bookings: [[-10, 11, 0, 't1', 'thao', 'no_show']] },
    { customerId: 'c6', pkgId: 'k5', soldAgo: 200, branchId: 'q10', consultant: 'Nhi', plan: [[0]], pays: [[0, 6_000_000, 'cash']],
      uses: [[190, [['t3', 1]], 'nhi'], [170, [['t3', 1]], 'nhi'], [150, [['t3', 1]], 'tuan'], [120, [['t3', 1]], 'nhi'], [80, [['t3', 1]], 'nhi'], [40, [['t3', 1]], 'nhi']] },
    { customerId: 'c8', pkgId: 'k6', soldAgo: 70, branchId: 'q1', consultant: 'Lan', discountPct: 5, plan: [[0, 25_000_000], [30, 20_000_000], [60, 25_000_000], [75]], pays: [[0, 25_000_000, 'transfer'], [29, 20_000_000, 'transfer'], [58, 25_000_000, 'card']],
      uses: [[65, [['s1', 1]], 'lan'], [50, [['s5', 1]], 'mai'], [40, [['s2', 1]], 'lan'], [35, [['s5', 1]], 'mai'], [20, [['s5', 1]], 'ha']], bookings: [[0, 15, 0, 's5', 'mai'], [1, 14, 0, 's2', 'lan']] },
    { customerId: 'c7', pkgId: 'k2', soldAgo: 300, branchId: 'q10', consultant: 'Tuấn', plan: [[0]], pays: [[0, 10_000_000, 'transfer']],
      uses: [[290, [['s6', 1]], 'nhi'], [270, [['s6', 1]], 'nhi'], [250, [['s6', 1], ['t4', 1]], 'tuan'], [230, [['s6', 1]], 'nhi'], [210, [['s6', 1]], 'nhi'], [190, [['s6', 1], ['t4', 1]], 'nhi'], [170, [['s6', 1]], 'tuan'], [150, [['s6', 1]], 'nhi'], [130, [['s6', 1]], 'nhi'], [110, [['s6', 1]], 'nhi']] },
    { customerId: 'c3', pkgId: 'k8', soldAgo: 150, branchId: 'q1', consultant: 'Mai', plan: [[0]], pays: [[0, 4_500_000, 'transfer']],
      uses: [[140, [['t5', 1]], 'mai'], [120, [['t5', 1]], 'ha']], refund: { ago: 100, feePct: 10, reason: 'Khách chuyển công tác ra Hà Nội, không tiếp tục liệu trình', method: 'transfer' } },
    { customerId: 'c1', pkgId: 'k3', soldAgo: 10, branchId: 'q1', consultant: 'Lan', plan: [[0, 3_000_000], [30]], pays: [[0, 3_000_000, 'cash']],
      uses: [[8, [['s5', 1]], 'ha']], bookings: [[0, 10, 30, 's5', 'ha']] },
    { customerId: 'c6', pkgId: 'k4', soldAgo: 160, branchId: 'q10', consultant: 'Nhi', plan: [[0, 1_600_000], [30, 1_600_000], [60]], pays: [[0, 1_600_000, 'cash']],
      uses: [[150, [['t1', 1]], 'nhi'], [120, [['t1', 1]], 'tuan']] },
  ];

  const customers = SEED_CUSTOMERS;
  const cards: TreatmentCard[] = specs.map(s => {
    const pkg = packages.find(p => p.id === s.pkgId)!;
    const cust = customers.find(c => c.id === s.customerId)!;
    const soldAt = at(-s.soldAgo, 10, 15);
    const saleDate = ymd(soldAt);
    const price = Math.round(pkg.price * (1 - (s.discountPct || 0) / 100) / 1000) * 1000;
    let planLeft = price;
    const installments: Installment[] = s.plan.map(([after, amount], i) => {
      const a = i === s.plan.length - 1 ? planLeft : amount!;
      planLeft -= a;
      return { no: i + 1, dueDate: addDaysYmd(saleDate, after), amount: a };
    });
    const id = next('card');
    const events: CardEvent[] = [{ at: soldAt, by: s.consultant, kind: 'issue', text: `Chuyển thẻ từ MySpa – giá ${fmt(price)}đ, ${installments.length} đợt thanh toán` }];
    const payments = s.pays.map(([after, amount, method]) => {
      const pAt = at(-s.soldAgo + after, 10, 30);
      events.push({ at: pAt, by: s.consultant, kind: 'payment', text: `Thu tiền đợt – ${method === 'cash' ? 'tiền mặt' : method === 'transfer' ? 'chuyển khoản' : method === 'card' ? 'quẹt thẻ' : 'ví điện tử'}`, amount });
      return { id: next('pay'), at: pAt, amount, method, txnRef: method === 'cash' ? '' : `FT${saleDate.replace(/-/g, '').slice(2)}${pad(seq.pay * 137, 5)}`, by: s.consultant, branchId: s.branchId };
    });
    const card: TreatmentCard = {
      id, customerId: cust.id, customerName: cust.name, customerPhone: cust.phone,
      packageId: pkg.id, packageCode: pkg.code, packageName: pkg.name, category: pkg.category, kind: pkg.kind,
      branchId: s.branchId, soldBy: s.consultant, consultant: s.consultant, createdAt: soldAt, ...issueValidity(pkg, saleDate),
      listPrice: pkg.price, price, lines: pkg.kind === 'session' ? buildLines(pkg, price) : [],
      faceValue: pkg.faceValue, valueAdjust: 0, valueUsedBefore: 0, allowedServiceIds: pkg.items.map(i => i.serviceId),
      installments, payments, usages: [], bookings: [], refunds: [], events, status: 'active', source: 'import', note: '', policy: policyOf(pkg),
    };
    s.uses.forEach(([ago, items, staffId], k) => {
      const uAt = at(-ago, 9 + (k % 8), (k * 17) % 60);
      const its = usageItems(card, items.map(([serviceId, qty]) => ({ serviceId, qty })));
      card.usages.push({ id: next('use'), at: uAt, branchId: staffBranch(staffId), staffId, items: its, by: staffLabel(staffId), note: '', signed: true });
      events.push({ at: uAt, by: staffLabel(staffId), kind: 'use', text: `Trừ ${its.map(i => `${i.name} ×${i.qty}`).join(', ')}`, amount: undefined });
    });
    (s.bookings || []).forEach(([inDays, h, m, serviceId, staffId, status]) => {
      const created = at(Math.min(-1, inDays - 3), 17);
      card.bookings.push({
        id: next('booking'), at: at(inDays, h, m), branchId: staffBranch(staffId), serviceId, name: serviceOf(serviceId)?.name || serviceId,
        staffId, note: '', status: status || 'booked', createdAt: created, by: 'Lễ tân',
      });
      events.push({ at: created, by: 'Lễ tân', kind: 'booking', text: `Đặt lịch ${serviceOf(serviceId)?.name} lúc ${pad(h, 2)}:${pad(m, 2)} ${addDaysYmd(ymd(now), inDays).split('-').reverse().join('/')}` });
    });
    if (s.refund) {
      const rAt = at(-s.refund.ago, 15);
      const paid = payments.reduce((x, p) => x + p.amount, 0);
      const consumed = card.lines.reduce((x, l) => x + Math.min(card.usages.flatMap(u => u.items).filter(i => i.serviceId === l.serviceId).reduce((y, i) => y + i.qty, 0), l.total) * l.unitValue, 0);
      const gross = Math.round(paid - consumed);
      const amount = Math.floor(gross * (1 - s.refund.feePct / 100) / 1000) * 1000;
      card.refunds.push({
        id: next('refund'), at: rAt, by: s.consultant, mode: 'full', sessions: [], valueReturned: 0, amount, fee: gross - amount,
        method: s.refund.method, txnRef: 'FT' + pad(seq.refund * 99173, 9), reason: s.refund.reason, status: 'paid', approvedBy: 'Quản lý Hà (Q1)', approvedAt: rAt,
      });
      events.push({ at: rAt, by: 'Quản lý Hà (Q1)', kind: 'refund', text: `Hoàn toàn bộ – phí ${fmt(gross - amount)}đ`, amount: -amount });
      card.status = 'refunded';
    }
    card.events = events.sort((a, b) => a.at.localeCompare(b.at));
    return card;
  });

  return { packages, cards: cards.reverse(), seq };
}

// ---------------------------------------------------------------------------

/** Mô tả các thay đổi về tiền của gói để lưu lịch sử giá */
export function pricingChanges(a: TreatmentPackage, b: TreatmentPackage): string[] {
  const out: string[] = [];
  const pa = pricingOf(a), pb = pricingOf(b);
  const m = (n: number) => `${fmt(n)}đ`;
  if (a.price !== b.price) out.push(`Giá niêm yết ${m(a.price)} → ${m(b.price)}`);
  if (a.faceValue !== b.faceValue) out.push(`Mệnh giá ${m(a.faceValue)} → ${m(b.faceValue)}`);
  const branches = Array.from(new Set([...Object.keys(pa.branchPrices), ...Object.keys(pb.branchPrices)]));
  branches.forEach(k => {
    const x = pa.branchPrices[k] || 0, y = pb.branchPrices[k] || 0;
    if (x !== y) out.push(`Giá chi nhánh ${k.toUpperCase()}: ${x ? m(x) : 'theo niêm yết'} → ${y ? m(y) : 'theo niêm yết'}`);
  });
  const promoText = (p: typeof pa.promo) => (p ? `${m(p.price)} (${p.from.split('-').reverse().join('/')}–${p.to.split('-').reverse().join('/')})` : 'không');
  if (JSON.stringify(pa.promo) !== JSON.stringify(pb.promo)) out.push(`Khuyến mãi: ${promoText(pa.promo)} → ${promoText(pb.promo)}`);
  if (pa.maxDiscountPct !== pb.maxDiscountPct) out.push(`Chiết khấu tối đa ${pa.maxDiscountPct}% → ${pb.maxDiscountPct}%`);
  if (a.maxInstallments !== b.maxInstallments || a.minFirstPct !== b.minFirstPct) out.push(`Trả góp ${a.maxInstallments} đợt/đợt 1 ≥ ${a.minFirstPct}% → ${b.maxInstallments} đợt/đợt 1 ≥ ${b.minFirstPct}%`);
  if (pa.installmentSurchargePct !== pb.installmentSurchargePct) out.push(`Phụ thu trả góp ${pa.installmentSurchargePct}% → ${pb.installmentSurchargePct}%`);
  if (pa.intervalDays !== pb.intervalDays) out.push(`Khoảng cách đợt ${pa.intervalDays} → ${pb.intervalDays} ngày`);
  if (pa.allowRefund !== pb.allowRefund || pa.refundFeePct !== pb.refundFeePct) out.push(`Hoàn tiền ${pa.allowRefund ? `phí ${pa.refundFeePct}%` : 'không cho'} → ${pb.allowRefund ? `phí ${pb.refundFeePct}%` : 'không cho'}`);
  if (pa.extensionFeePerMonth !== pb.extensionFeePerMonth || pa.maxExtensionMonths !== pb.maxExtensionMonths) out.push(`Gia hạn ${m(pa.extensionFeePerMonth)}/tháng, tối đa ${pa.maxExtensionMonths} tháng → ${m(pb.extensionFeePerMonth)}/tháng, tối đa ${pb.maxExtensionMonths} tháng`);
  return out;
}

const mapCard = (cards: TreatmentCard[], id: string, fn: (c: TreatmentCard) => TreatmentCard) => cards.map(c => (c.id === id ? fn(c) : c));
const ev = (c: TreatmentCard, e: CardEvent): CardEvent[] => [...c.events, e];
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); };

const useTreatmentStore = create<TreatmentState>()(
  persist(
    (set, get) => ({
      ...buildSeed(),
      config: DEFAULT_CONFIG,
      syncSince: startOfToday(),
      payRequests: [],
      reqSeq: 0,

      upsertPackage: (p, by) => set(st => {
        const old = st.packages.find(x => x.id === p.id);
        const changes = old ? pricingChanges(old, p) : ['Tạo gói'];
        const next = changes.length ? { ...p, priceHistory: [...(old?.priceHistory || []), { at: new Date().toISOString(), by, changes }] } : p;
        return { packages: old ? st.packages.map(x => (x.id === p.id ? next : x)) : [next, ...st.packages] };
      }),
      togglePackage: id => set(st => ({ packages: st.packages.map(p => (p.id === id ? { ...p, active: !p.active, updatedAt: new Date().toISOString() } : p)) })),
      setConfig: patch => set(st => ({ config: { ...st.config, ...patch } })),

      reconcileWithPos: snap => {
        const s = get();
        const seq = { ...s.seq };
        const { cards, issued } = reconcile(s.cards, snap, {
          packages: s.packages, cfg: s.config, since: s.syncSince, nextId: k => idOf(k, ++seq[k]),
        });
        if (cards) set({ cards: [...issued.filter(i => !s.cards.some(c => c.id === i.id)), ...cards.filter(c => !issued.some(i => i.id === c.id))], seq });
        return issued;
      },

      issueCard: ({ order, lineKey, customer, plan, consultant, by }) => {
        const s = get();
        const line = order.lines.find(l => l.key === lineKey);
        const pkg = line && s.packages.find(p => p.id === line.itemId);
        if (!line || !pkg) return null;
        const existing = s.cards.find(c => c.orderId === order.id && c.lineKey === lineKey);
        if (existing) return existing;
        const card = cardFromOrder(idOf('card', s.seq.card + 1), order, line, pkg, customer, { plan, consultant, by });
        set(st => ({ cards: [card, ...st.cards], seq: { ...st.seq, card: st.seq.card + 1 } }));
        return card;
      },

      setPlan: (cardId, plan, by, reason) => set(st => ({
        cards: mapCard(st.cards, cardId, c => ({
          ...c, installments: plan,
          events: ev(c, { at: new Date().toISOString(), by, kind: 'plan', text: `Sửa lịch thanh toán: ${plan.length} đợt${reason ? ` – ${reason}` : ''}` }),
        })),
      })),

      recordPayment: (cardId, p) => set(st => {
        const at = new Date().toISOString();
        const id = idOf('pay', st.seq.pay + 1);
        return {
          cards: mapCard(st.cards, cardId, c => ({
            ...c, payments: [...c.payments, { id, at, ...p, txnRef: p.txnRef.trim().toUpperCase() }],
            events: ev(c, { at, by: p.by, kind: 'payment', text: `Thu tiền đợt (${id})`, amount: p.amount }),
          })),
          seq: { ...st.seq, pay: st.seq.pay + 1 },
        };
      }),

      deduct: (cardId, input) => {
        const s = get();
        const card = s.cards.find(c => c.id === cardId);
        if (!card) return null;
        const at = new Date().toISOString();
        const items = usageItems(card, input.items);
        const usage: CardUsage = {
          id: idOf('use', s.seq.use + 1), at, branchId: input.branchId, staffId: input.staffId, items, by: input.by, note: input.note.trim(),
          signed: input.signed, bookingId: input.bookingId, overrideBy: input.overrideBy, overrideReason: input.overrideReason,
        };
        const text = `Trừ ${items.map(i => `${i.name} ×${i.qty}`).join(', ')}${card.kind === 'value' ? ` (-${fmt(usage.items.reduce((x, i) => x + i.value, 0))}đ)` : ''}${input.overrideBy ? ` – ${input.overrideBy} duyệt vượt quy tắc` : ''}`;
        // Thẻ tính hạn từ buổi đầu: lần trừ đầu tiên kích hoạt thẻ, hạn dùng bắt đầu từ hôm nay
        const activation = activationOf(card, at);
        set(st => ({
          cards: mapCard(st.cards, cardId, c => {
            const used = ev(c, { at, by: input.by, kind: 'use', text });
            return {
              ...c, ...activation, usages: [...c.usages, usage],
              bookings: input.bookingId ? c.bookings.map(b => (b.id === input.bookingId ? { ...b, status: 'done', usageId: usage.id } : b)) : c.bookings,
              events: activation
                ? [...used, { at, by: input.by, kind: 'issue', text: `Kích hoạt thẻ ở buổi dùng đầu – hạn dùng đến ${activation.expiresAt.split('-').reverse().join('/')}` }]
                : used,
            };
          }),
          seq: { ...st.seq, use: st.seq.use + 1 },
        }));
        return usage;
      },

      cancelUsage: (cardId, usageId, reason, by) => set(st => {
        const at = new Date().toISOString();
        return {
          cards: mapCard(st.cards, cardId, c => {
            const u = c.usages.find(x => x.id === usageId);
            return {
              ...c,
              usages: c.usages.map(x => (x.id === usageId ? { ...x, cancelled: { at, by, reason } } : x)),
              bookings: u?.bookingId ? c.bookings.map(b => (b.id === u.bookingId ? { ...b, status: 'booked', usageId: undefined } : b)) : c.bookings,
              events: ev(c, { at, by, kind: 'cancel_use', text: `Hủy lượt ${usageId}, hoàn lại ${u?.items.map(i => `${i.name} ×${i.qty}`).join(', ')}: ${reason}` }),
            };
          }),
        };
      }),

      extendCard: (cardId, { expiresAt, months, fee, reason, by }) => set(st => ({
        cards: mapCard(st.cards, cardId, c => ({
          ...c, expiresAt, extendedMonths: (c.extendedMonths || 0) + months,
          events: ev(c, { at: new Date().toISOString(), by, kind: 'extend', text: `Gia hạn từ ${c.expiresAt.split('-').reverse().join('/')} đến ${expiresAt.split('-').reverse().join('/')}${fee ? ` – phí ${fmt(fee)}đ` : ''}: ${reason}` }),
        })),
      })),

      adjustCard: (cardId, { serviceId, delta, reason, by }) => set(st => ({
        cards: mapCard(st.cards, cardId, c => {
          const at = new Date().toISOString();
          if (c.kind === 'value') {
            return { ...c, valueAdjust: c.valueAdjust + delta, events: ev(c, { at, by, kind: 'adjust', text: `Điều chỉnh số dư ${delta > 0 ? '+' : ''}${fmt(delta)}đ: ${reason}` }) };
          }
          const svc = serviceOf(serviceId || '');
          const has = c.lines.some(l => l.serviceId === serviceId);
          const lines = has
            ? c.lines.map(l => (l.serviceId === serviceId ? { ...l, bonus: l.bonus + delta } : l))
            : [...c.lines, { serviceId: serviceId!, name: svc?.name || serviceId!, group: svc?.group || '', role: 'bonus' as const, total: 0, bonus: delta, usedBefore: 0, unitValue: 0 }];
          const name = lines.find(l => l.serviceId === serviceId)?.name;
          return { ...c, lines, events: ev(c, { at, by, kind: 'adjust', text: `${delta > 0 ? 'Tặng thêm' : 'Giảm'} ${Math.abs(delta)} buổi ${name}: ${reason}` }) };
        }),
      })),

      createBooking: (cardId, input) => {
        const s = get();
        const booking: CardBooking = {
          ...input, id: idOf('booking', s.seq.booking + 1), name: serviceOf(input.serviceId)?.name || input.serviceId,
          status: 'booked', createdAt: new Date().toISOString(),
        };
        const d = new Date(booking.at);
        set(st => ({
          cards: mapCard(st.cards, cardId, c => ({
            ...c, bookings: [...c.bookings, booking],
            events: ev(c, { at: booking.createdAt, by: input.by, kind: 'booking', text: `Đặt lịch ${booking.name} lúc ${pad(d.getHours(), 2)}:${pad(d.getMinutes(), 2)} ${pad(d.getDate(), 2)}/${pad(d.getMonth() + 1, 2)}/${d.getFullYear()}` }),
          })),
          seq: { ...st.seq, booking: st.seq.booking + 1 },
        }));
        return booking;
      },

      setBookingStatus: (cardId, bookingId, status, by, reason) => set(st => ({
        cards: mapCard(st.cards, cardId, c => ({
          ...c,
          bookings: c.bookings.map(b => (b.id === bookingId ? { ...b, status, cancelReason: reason } : b)),
          events: ev(c, { at: new Date().toISOString(), by, kind: 'booking', text: `${status === 'cancelled' ? 'Hủy' : status === 'no_show' ? 'Khách không đến –' : 'Cập nhật'} lịch hẹn ${bookingId}${reason ? `: ${reason}` : ''}` }),
        })),
      })),

      addRefund: (cardId, r) => {
        const s = get();
        const at = new Date().toISOString();
        const refund: CardRefund = { ...r, id: idOf('refund', s.seq.refund + 1), at };
        const closes = refund.mode === 'full' && refund.status === 'paid';
        set(st => ({
          cards: mapCard(st.cards, cardId, c => ({
            ...c, refunds: [...c.refunds, refund], status: closes ? 'refunded' as CardBaseStatus : c.status,
            events: ev(c, {
              at, by: r.by, kind: 'refund',
              text: `${refund.mode === 'full' ? 'Hoàn toàn bộ' : 'Hoàn một phần'}${refund.sessions.length ? ` (${refund.sessions.map(x => `${serviceOf(x.serviceId)?.name} ×${x.qty}`).join(', ')})` : ''}${refund.posRefundId ? ` – phiếu POS ${refund.posRefundId}` : ''} – ${refund.status === 'paid' ? 'đã chi' : 'chờ duyệt'}`,
              amount: refund.status === 'paid' ? -refund.amount : undefined,
            }),
          })),
          seq: { ...st.seq, refund: st.seq.refund + 1 },
        }));
        return refund;
      },

      approveRefund: (cardId, refundId, by) => set(st => ({
        cards: mapCard(st.cards, cardId, c => {
          const at = new Date().toISOString();
          const r = c.refunds.find(x => x.id === refundId)!;
          return {
            ...c,
            refunds: c.refunds.map(x => (x.id === refundId ? { ...x, status: 'paid', approvedBy: by, approvedAt: at } : x)),
            status: r.mode === 'full' ? 'refunded' : c.status,
            events: ev(c, { at, by, kind: 'refund', text: `Duyệt và chi hoàn ${refundId}`, amount: -r.amount }),
          };
        }),
      })),

      rejectRefund: (cardId, refundId, by, reason) => set(st => ({
        cards: mapCard(st.cards, cardId, c => ({
          ...c,
          refunds: c.refunds.map(x => (x.id === refundId ? { ...x, status: 'rejected', approvedBy: by, approvedAt: new Date().toISOString(), rejectReason: reason } : x)),
          events: ev(c, { at: new Date().toISOString(), by, kind: 'refund', text: `Từ chối phiếu hoàn ${refundId}: ${reason}` }),
        })),
      })),

      logPrint: (cardId, by, text) => set(st => ({ cards: mapCard(st.cards, cardId, c => ({ ...c, events: ev(c, { at: new Date().toISOString(), by, kind: 'print', text }) })) })),

      createPayRequest: ({ cardId, amount, installmentLabel, branchId, note, by }) => {
        const s = get();
        const card = s.cards.find(c => c.id === cardId);
        if (!card) return null;
        const pending = s.payRequests.find(r => r.cardId === cardId && r.status === 'pending');
        if (pending) return pending;
        const at = new Date().toISOString();
        const n = (s.reqSeq || 0) + 1;
        const req: PayRequest = {
          id: `YC-${pad(n, 6)}`, cardId, customerName: card.customerName, customerPhone: card.customerPhone, packageName: card.packageName,
          installmentLabel, amount, branchId, note: note.trim(), createdAt: at, createdBy: by, status: 'pending',
        };
        set(st => ({
          payRequests: [req, ...st.payRequests],
          reqSeq: n,
          cards: mapCard(st.cards, cardId, c => ({ ...c, events: ev(c, { at, by, kind: 'payment', text: `Gửi yêu cầu thu ${fmt(amount)}đ (${installmentLabel}) sang POS – ${req.id}` }) })),
        }));
        return req;
      },

      cancelPayRequest: (id, by, reason) => set(st => {
        const r = st.payRequests.find(x => x.id === id);
        if (!r || r.status !== 'pending') return {};
        const at = new Date().toISOString();
        return {
          payRequests: st.payRequests.map(x => (x.id === id ? { ...x, status: 'cancelled', resolvedAt: at, resolvedBy: by, reason } : x)),
          cards: mapCard(st.cards, r.cardId, c => ({ ...c, events: ev(c, { at, by, kind: 'payment', text: `Hủy yêu cầu thu tiền ${id}${reason ? `: ${reason}` : ''}` }) })),
        };
      }),

      rejectPayRequest: (id, by, reason) => set(st => {
        const r = st.payRequests.find(x => x.id === id);
        if (!r || r.status !== 'pending') return {};
        const at = new Date().toISOString();
        return {
          payRequests: st.payRequests.map(x => (x.id === id ? { ...x, status: 'rejected', resolvedAt: at, resolvedBy: by, reason } : x)),
          cards: mapCard(st.cards, r.cardId, c => ({ ...c, events: ev(c, { at, by, kind: 'payment', text: `POS từ chối yêu cầu ${id}: ${reason}` }) })),
        };
      }),

      completePayRequest: (id, res) => set(st => ({
        payRequests: st.payRequests.map(x => (x.id === id && x.status === 'pending' ? {
          ...x, status: 'paid', resolvedAt: new Date().toISOString(), resolvedBy: res.by, paymentId: res.paymentId, amountPaid: res.amountPaid,
          method: res.method, txnRef: res.txnRef, change: res.change, tendered: res.tendered,
        } : x)),
      })),

      resetDemo: () => set({ ...buildSeed(), config: DEFAULT_CONFIG, syncSince: startOfToday(), payRequests: [], reqSeq: 0 }),
    }),
    {
      name: TREATMENT_STORAGE_KEY,
      version: 2,
      // v2: gói có quy định tiền riêng — gói mẫu đã lưu từ bản trước được bổ sung quy định mẫu
      migrate: (state, version) => {
        const s = state as TreatmentState;
        if (version < 2 && s?.packages) {
          const seed = seedPackages(new Date());
          s.packages = s.packages.map(p => (p.pricing ? p : { ...p, pricing: seed.find(x => x.id === p.id)?.pricing || {}, priceHistory: p.priceHistory || [] }));
        }
        return s;
      },
    },
  ),
);

// Đồng bộ giữa các tab trình duyệt (mô phỏng realtime khi chưa có backend)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', e => {
    if (e.key === TREATMENT_STORAGE_KEY) useTreatmentStore.persist.rehydrate();
  });
}

export default useTreatmentStore;
